/**
 * WebRTC video call quality tracker.
 * Collects per-second send/receive metrics and updates a UI element with a traffic-light indicator.
 */

const SKIP_INITIAL_SAMPLES = 1;
const PERIOD_MS = 60000;

let qualityLog = {};
let qualityInterval = null;
let lastReportTimestamp = 0;
let isTracking = false
/**
 * Start tracking video quality for a given session.
 * @param {HTMLElement} videoQualityElement - DOM element whose background color reflects quality.
 * @param chat
 */
export function trackVideoQuality(videoQualityElement, chat) {
  console.warn("START TRACKING VIDEO QUALITY")
  if (qualityInterval) clearInterval(qualityInterval);
  const pc = chat.session?.sessionDescriptionHandler?.peerConnection;
  if (!pc) return;
  isTracking = true
  qualityLog = {
    startTimestamp: Date.now(),
    endTimestamp: null,
    samples: [],
    _skipCount: 0,
    interactionQualitySummary: [],
    lastSummaryTimestamp: Date.now(),
    _lastSummaryIndex: 0,
    chatId: chat.chatStored.chatId.split('-')[1]
  };

  lastReportTimestamp = Date.now();
  let lastInbound = { timestamp: 0, bytes: 0, frames: 0, dropped: 0 };
  let lastOutbound = { timestamp: 0, bytes: 0, frames: 0, qpSum: 0 };
  let initialOutboundWidth = null;
  let initialOutboundHeight = null;

  qualityInterval = setInterval(async () => {
    const statsReport = await pc.getStats();
    const parsed = parseVideoStats(statsReport, lastInbound, lastOutbound, initialOutboundWidth, initialOutboundHeight);
    if (!parsed) return;

    const { metrics, newInbound, newOutbound, newInitialWidth, newInitialHeight } = parsed;
    lastInbound = newInbound;
    lastOutbound = newOutbound;
    initialOutboundWidth = newInitialWidth;
    initialOutboundHeight = newInitialHeight;
    const recvScore = scoreReceive(metrics);
    const sendScore = scoreSend(metrics, initialOutboundWidth, initialOutboundHeight);
    const quality = mergeScores(recvScore, sendScore);
    logQualityIssues(quality, recvScore, sendScore, metrics, initialOutboundWidth, initialOutboundHeight);
    updateQualityUI(quality, videoQualityElement);

    qualityLog._skipCount++;
    if (qualityLog._skipCount > SKIP_INITIAL_SAMPLES) {
      const reasons = (quality === 'good')
        ? []
        : getQualityReasons(recvScore, sendScore, metrics, initialOutboundWidth, initialOutboundHeight);
      qualityLog.samples.push({
        timestamp: Date.now(),
        quality,
        reasons,
        inbound: {
          packetLossFraction: metrics.packetLossFraction,
          jitterMilliseconds: metrics.jitterMilliseconds,
          roundTripTimeMilliseconds: metrics.roundTripTimeMilliseconds,
          inboundBitrate: metrics.inboundBitrate,
          inboundFrameRate: metrics.inboundFrameRate,
          inboundDropRate: metrics.inboundDropRate
        },
        outbound: {
          outboundBitrate: metrics.outboundBitrate,
          outboundFrameRate: metrics.outboundFrameRate,
          outboundFrameWidth: metrics.outboundFrameWidth,
          outboundFrameHeight: metrics.outboundFrameHeight,
          outboundAverageQp: metrics.outboundAverageQp,
          targetBitrateBps: metrics.targetBitrateBps
        }
      });
    }
    // Every minute, compute and store summary of the samples since last summary
    if (Date.now() - qualityLog.lastSummaryTimestamp >= PERIOD_MS) {
      const periodSamples = qualityLog.samples.slice(qualityLog._lastSummaryIndex);
      const periodSummary = _computeStatsSummary(periodSamples);
      qualityLog.interactionQualitySummary.push({ timestamp: Date.now(), summary: periodSummary });
      qualityLog._lastSummaryIndex = qualityLog.samples.length;
      qualityLog.lastSummaryTimestamp = Date.now();
      const fullSummary    = _computeStatsSummary(qualityLog.samples);
      const overallQuality = computeOverallQuality(qualityLog.samples);
      const logData = {
        startTimestamp: qualityLog.startTimestamp,
        interactionQualitySummary: {
          ...fullSummary,
          overallQuality
        }
      };
      _sendQualityLogToServer(chat,logData);
      // update the send-marker
      lastReportTimestamp = qualityLog.lastSummaryTimestamp;
    }
  }, 1000)
}

/**
 * Parse RTC stats into structured metrics and update snapshots.
 * @param {RTCStatsReport} statsReport
 * @param {Object} lastInbound
 * @param {Object} lastOutbound
 * @param {number|null} initialWidth
 * @param {number|null} initialHeight
 * @returns {Object|null} { metrics, newInbound, newOutbound, newInitialWidth, newInitialHeight } or null if stats incomplete.
 */
function parseVideoStats(statsReport, lastInbound, lastOutbound, initialWidth, initialHeight) {
  let inbound, outbound, candidatePair, targetBitrateBps = null;
  statsReport.forEach(r => {
    if (r.type==='inbound-rtp'&&r.kind==='video') inbound=r;
    if (r.type==='outbound-rtp'&&r.kind==='video') {
      outbound=r;
      if (typeof r.targetBitrate==='number') targetBitrateBps=r.targetBitrate;
    }
    if (r.type==='candidate-pair'&&r.nominated) candidatePair=r;
  });
  if (!inbound||!candidatePair) return null;

  const now = Date.now();
  // Inbound deltas
  const dtIn = (inbound.timestamp - lastInbound.timestamp)/1000;
  const inboundBitrate = dtIn>0 ? (inbound.bytesReceived - lastInbound.bytes)*8/dtIn : 0;
  const inboundFrameRate = dtIn>0 ? (inbound.framesDecoded - lastInbound.frames)/dtIn : 0;
  const deltaDropped = inbound.framesDropped - lastInbound.dropped;
  const deltaDecoded = inbound.framesDecoded - lastInbound.frames;
  const inboundDropRate = (deltaDropped >= 0 && deltaDecoded + deltaDropped > 0)
    ? deltaDropped / (deltaDecoded + deltaDropped)
    : 0;

  const metrics = {
    packetLossFraction: inbound.packetsLost/(inbound.packetsLost+inbound.packetsReceived)||0,
    jitterMilliseconds: inbound.jitter*1000,
    roundTripTimeMilliseconds: candidatePair.currentRoundTripTime*1000,
    inboundBitrate,
    inboundFrameRate,
    inboundDropRate,
    outboundBitrate: 0,
    outboundFrameRate: 0,
    outboundFrameWidth: null,
    outboundFrameHeight: null,
    outboundAverageQp: null,
    targetBitrateBps
  };

  // Outbound deltas
  let newInitialWidth = initialWidth, newInitialHeight = initialHeight;
  const newInbound = {
    timestamp: inbound.timestamp,
    bytes: inbound.bytesReceived,
    frames: inbound.framesDecoded,
    dropped: inbound.framesDropped
  };
  let newOutbound = lastOutbound;
  if (outbound) {
    const dtOut = (outbound.timestamp - lastOutbound.timestamp)/1000;
    metrics.outboundBitrate = dtOut>0 ? (outbound.bytesSent - lastOutbound.bytes)*8/dtOut : 0;
    metrics.outboundFrameRate = dtOut>0 ? (outbound.framesEncoded - lastOutbound.frames)/dtOut : 0;
    if (typeof outbound.qpSum==='number' && outbound.framesEncoded>lastOutbound.frames) {
      metrics.outboundAverageQp = (outbound.qpSum - lastOutbound.qpSum)/(outbound.framesEncoded - lastOutbound.frames);
    }
    metrics.outboundFrameWidth  = outbound.frameWidth  ?? null;
    metrics.outboundFrameHeight = outbound.frameHeight ?? null;
    if (!initialWidth && metrics.outboundFrameWidth && metrics.outboundFrameHeight) {
      newInitialWidth = metrics.outboundFrameWidth;
      newInitialHeight = metrics.outboundFrameHeight;
    }
    newOutbound = { timestamp: outbound.timestamp, bytes: outbound.bytesSent, frames: outbound.framesEncoded, qpSum: outbound.qpSum||lastOutbound.qpSum };
  }

  return { metrics, newInbound, newOutbound, newInitialWidth, newInitialHeight };
}

/**
 * Score receive-side quality.
 * @param {Object} m - metrics from parseVideoStats
 * @returns {'good'|'fair'|'poor'}
 */
function scoreReceive(m) {
  const good = m.packetLossFraction<0.01 && m.jitterMilliseconds<50 && m.roundTripTimeMilliseconds<150 && m.inboundFrameRate>22 && m.inboundDropRate<0.02;
  const fair = m.packetLossFraction<0.03 && m.jitterMilliseconds<80 && m.roundTripTimeMilliseconds<300 && m.inboundFrameRate>15 && m.inboundDropRate<0.05;
  return good?'good':fair?'fair':'poor';
}

/**
 * Score send-side quality.
 * @param {Object} m - metrics from parseVideoStats
 * @param {number|null} initWidth
 * @param {number|null} initHeight
 * @returns {'good'|'fair'|'poor'}
 */
function scoreSend(m, initWidth, initHeight) {
  const good = m.outboundFrameRate>22
    && (initWidth?m.outboundFrameWidth>=initWidth*0.8:true)
    && (initHeight?m.outboundFrameHeight>=initHeight*0.8:true)
    && (m.outboundAverageQp!=null?m.outboundAverageQp<40:true);
  const fair = m.outboundFrameRate>15
    && (initWidth?m.outboundFrameWidth>=initWidth*0.5:true)
    && (initHeight?m.outboundFrameHeight>=initHeight*0.5:true)
    && (m.outboundAverageQp!=null?m.outboundAverageQp<50:true);
  return good?'good':fair?'fair':'poor';
}

/**
 * Merge receive/send scores by worst-of.
 * @param {'good'|'fair'|'poor'} recv
 * @param {'good'|'fair'|'poor'} send
 * @returns {'good'|'fair'|'poor'}
 */
function mergeScores(recv, send) {
  if (recv==='poor'||send==='poor') return 'poor';
  if (recv==='fair'||send==='fair') return 'fair';
  return 'good';
}

/**
 * Log which metrics caused a non-good quality.
 * @param {'good'|'fair'|'poor'} quality
 * @param {'good'|'fair'|'poor'} recvScore
 * @param {'good'|'fair'|'poor'} sendScore
 * @param {Object} m - metrics
 * @param {number|null} initWidth
 * @param {number|null} initHeight
 */
function logQualityIssues(quality, recvScore, sendScore, m, initWidth, initHeight) {
  if (quality==='good') return;
  const fail = [];
  // receive failures
  if (recvScore!=='good') {
    const baseBr = m.targetBitrateBps;
    const low = baseBr<300000;
    const goodBr = low?0:baseBr*0.8;
    if (m.packetLossFraction>=0.01) fail.push(`loss ${(m.packetLossFraction*100).toFixed(2)}%`);
    if (m.jitterMilliseconds>=50)    fail.push(`jitter ${m.jitterMilliseconds.toFixed(0)}ms`);
    if (m.roundTripTimeMilliseconds>=150) fail.push(`RTT ${m.roundTripTimeMilliseconds.toFixed(0)}ms`);
    if (!low && m.inboundBitrate<=goodBr) fail.push(`inBitrate ${(m.inboundBitrate/1000).toFixed(0)}kbps`);
    if (m.inboundFrameRate<=22)       fail.push(`inFPS ${m.inboundFrameRate.toFixed(1)}`);
    if (m.inboundDropRate>=0.02)      fail.push(`drop ${(m.inboundDropRate*100).toFixed(1)}%`);
  }
  // send failures
  if (sendScore!=='good') {
    const low = m.outboundBitrate<300000;
    const goodBr = low?0:m.outboundBitrate*0.8;
    if (!low && m.outboundBitrate<=goodBr) fail.push(`outBitrate ${(m.outboundBitrate/1000).toFixed(0)}kbps`);
    if (m.outboundFrameRate<=22)       fail.push(`outFPS ${m.outboundFrameRate.toFixed(1)}`);
    if (initWidth && m.outboundFrameWidth<initWidth*0.8) fail.push(`resolution ${m.outboundFrameWidth}×${m.outboundFrameHeight}`);
    if (m.outboundAverageQp!=null&&m.outboundAverageQp>=40) fail.push(`QP ${m.outboundAverageQp.toFixed(1)}`);
  }
}

/**
 * Return an array of failure reasons for non-good quality.
 * @param {'good'|'fair'|'poor'} recvScore
 * @param {'good'|'fair'|'poor'} sendScore
 * @param {Object} m - metrics
 * @param {number|null} initWidth
 * @param {number|null} initHeight
 * @returns {string[]} list of failure descriptions
 */
function getQualityReasons(recvScore, sendScore, m, initWidth, initHeight) {
  const fail = [];
  // receive failures
  if (recvScore !== 'good') {
    if (m.packetLossFraction >= 0.01) fail.push(`loss ${(m.packetLossFraction*100).toFixed(2)}%`);
    if (m.jitterMilliseconds >= 50)    fail.push(`jitter ${m.jitterMilliseconds.toFixed(0)}ms`);
    if (m.roundTripTimeMilliseconds >= 150) fail.push(`RTT ${m.roundTripTimeMilliseconds.toFixed(0)}ms`);
    // Bitrate checks removed
    if (m.inboundFrameRate <= 22)       fail.push(`inFPS ${m.inboundFrameRate.toFixed(1)}`);
    if (m.inboundDropRate >= 0.02)      fail.push(`drop ${(m.inboundDropRate*100).toFixed(1)}%`);
  }
  // send failures
  if (sendScore !== 'good') {
    // Bitrate checks removed
    if (m.outboundFrameRate <= 22)       fail.push(`outFPS ${m.outboundFrameRate.toFixed(1)}`);
    if (initWidth && m.outboundFrameWidth < initWidth*0.8)
      fail.push(`resolution ${m.outboundFrameWidth}×${m.outboundFrameHeight}`);
    if (m.outboundAverageQp != null && m.outboundAverageQp >= 40)
      fail.push(`QP ${m.outboundAverageQp.toFixed(1)}`);
  }
  return fail;
}

/**
 * Update the background color of an element based on quality.
 * @param {'good'|'fair'|'poor'} quality
 * @param {HTMLElement} element
 */
function updateQualityUI(quality, element) {
  element.style.backgroundColor = { good:'#4CAF50', fair:'#FF9800', poor:'#F44336' }[quality]||'#888';
}

/**
 * Stop tracking video quality and upload the log.
 * @param chat
 */
export function stopVideoQualityTracking(chat) {
  if(!isTracking){
    return
  }
  console.warn("STOPPING VIDEO QUALITY TRACKING");

  if (qualityInterval) clearInterval(qualityInterval);
  qualityLog.endTimestamp = Date.now();
  qualityLog.summary = {
    inbound: computeStatsSummary(qualityLog.samples.map(s => ({ timestamp: s.timestamp, ...s.inbound }))),
    outbound: computeStatsSummary(qualityLog.samples.map(s => ({ timestamp: s.timestamp, ...s.outbound })))
  };
  const fullSummary    = _computeStatsSummary(qualityLog.samples);
  const overallQuality = computeOverallQuality(qualityLog.samples);
  const logData = {
    startTimestamp: qualityLog.startTimestamp,
    interactionQualitySummary: {
      ...fullSummary,
      overallQuality
    }
  };
  _sendQualityLogToServer(chat, logData);
  // Clear qualityLog after sending
  qualityLog = {};
  isTracking = false
}

/**
 * Upload the quality log to the server via RPC.
 */
async function _sendQualityLogToServer(chat, log) {
  try {
    let message = {
      "type" : "COMMAND",
      "action" : "interactionQualityLog",
      interactionType: "CHAT_VIDEO",
      // TOdo ovdje mozda bude falio interactionId
      interactionId: chat.chatStored.chatId.split('-')[1],
      logData: log
    }

    chat.sendMessage(message)
  } catch (err) {
    console.error('Quality log upload failed', err);
  }
}

/**
 * Compute min, max, and average for each numeric metric in samples.
 * @param {Array<Object>} samples
 * @returns {Object} summary object mapping metric names to {min, max, avg}
 */
function computeStatsSummary(samples) {
  const summary = {};
  if (!samples || samples.length === 0) return summary;
  const keys = Object.keys(samples[0]).filter(k => k !== 'timestamp');
  keys.forEach(key => {
    const values = samples.map(s => s[key]).filter(v => typeof v === 'number');
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    summary[key] = { min, max, avg };
  });
  return summary;
}
/**
 * Compute min, max, and average for each numeric metric in samples.
 * @param {Array<Object>} samples
 * @returns {Object} summary object mapping metric names to {min, max, avg}
 */
function _computeStatsSummary(samples) {
  const summary = {};
  if (!samples || samples.length === 0) return summary;
  // If the sample objects are nested (as in the main samples), flatten them
  const flatten = s => {
    let o = {};
    if (s.inbound) Object.assign(o, s.inbound);
    if (s.outbound) Object.assign(o, s.outbound);
    Object.keys(s).forEach(k => {
      if (k !== 'inbound' && k !== 'outbound' && k !== 'timestamp' && typeof s[k] === 'number')
        o[k] = s[k];
    });
    return o;
  };
  const flatSamples = samples.map(flatten);
  const keys = Object.keys(flatSamples[0]);
  keys.forEach(key => {
    const values = flatSamples.map(s => s[key]).filter(v => typeof v === 'number');
    if (values.length > 0) {
      const min = Math.min(...values);
      const max = Math.max(...values);
      const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
      summary[key] = { min, max, avg };
    }
  });
  return summary;
}

/**
 * Determine overall quality from all samples:
 *   – if any 'poor' → 'poor'
 *   – else if any 'fair' → 'fair'
 *   – else 'good'
 */
function computeOverallQuality(samples) {
  const counts = { poor: 0, fair: 0, good: 0 };
  const total = samples.length;

  for (const sample of samples) {
    counts[sample.quality] = (counts[sample.quality] || 0) + 1;
  }

  if (counts.poor / total > 0.5) return 'poor';
  if ((counts.poor + counts.fair) / total > 0.5) return 'fair';
  return 'good';
}